import { validateEmail } from 'utils/email';
import request from 'utils/request';

type TeamShareResponse = { status: 'ok' | 'fail' };

enum InviteTeamMemberErrorType {
  ValidateEmail = 'validateEmail',
  CheckUserTeamMembers = 'checkUserTeamMembers',
  CheckUsageTeamMembers = 'checkUsageTeamMembers',
  AddTeamSeats = 'addTeamSeats',
  AddTeamMember = 'addTeamMember',
  OtherError = 'otherError',
}

/**
 * Add the fetcher to use the team share API
 * It is used to share the infograph with the team
 * @param id e1 infograph uuid
 * @param template_id template uuid or 'blank'
 * @returns {Response} { status: 'ok' }
 */
export const shareWithTeam = async (id: string, template_id?: string) => {
  const formData = new URLSearchParams();
  formData.append('multipage', '1');

  return await request<TeamShareResponse>(`/share/team/${id}/${template_id ?? 'blank'}?from=Editor`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formData,
  });
};

/**
 * Add the fetcher to use the team unshare API
 * It is used to un share the infograph with the team
 * @param id e1 infograph uuid
 * @returns {Response} { status: 'ok' }
 */
export const unShareWithTeam = async (id: string) => {
  const formData = new URLSearchParams();
  formData.append('multipage', '1');
  return await request<TeamShareResponse>(`/unshare/team/${id}?from=Editor`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formData,
  });
};

type CheckUserTeamMembersResponse = { status: 'ok' | 'fail' };

/**
 * Check if a User already has a Business account
 * To know if we can add it as a Business member
 * @param email
 * @returns
 */
const checkUserTeamMembers = async (email: string) => {
  const formData = new URLSearchParams();
  formData.append('email', email);
  formData.append('enableTrial', 'false');
  return await request<CheckUserTeamMembersResponse>(`/team/members/check`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formData,
  });
};

type CheckUsageTeamMembersResponse = { status: 'ok' | 'fail'; rooms: number };

/**
 * Return the number of seats available in a Business Team
 * @param email
 * @returns
 */
export const checkUsageTeamMembers = async (email: string) => {
  return await request<CheckUsageTeamMembersResponse>(`/team/members/usage`, {
    method: 'GET',
  });
};

type AddTeamSeatsResponse = { status: 'ok' | 'fail' };

/**
 * Endpoint for increasing a Business team size
 * Equivalent to add_business_members in susbcription.php
 * @returns
 */
const addTeamSeats = async () => {
  return await request<AddTeamSeatsResponse>(`/vgaccount/SubscriptionV1/addTeamSeats`, {
    method: 'POST',
    body: JSON.stringify({
      count: 1,
      enableTrial: false,
    }),
  });
};

/**
 * Request to add the team member by email
 * @param email
 * @returns
 */
const addTeamMember = async (email: string) => {
  const formData = new URLSearchParams();
  formData.append('email', email);
  formData.append('enableTrial', 'false');
  return await request<CheckUsageTeamMembersResponse>(`/team/members/add`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formData,
  });
};

/**
 * Invite
 * @param email
 * @returns {<{status: boolean, errType: string}>} return status and errorType
 */
export const requestInviteTeamMember = async (email: string) => {
  try {
    if (!validateEmail(email)) return { status: false, errType: InviteTeamMemberErrorType.ValidateEmail };

    const checkUserRes = await checkUserTeamMembers(email);
    if (checkUserRes.status !== 'ok') {
      return { status: false, errType: InviteTeamMemberErrorType.CheckUserTeamMembers };
    }

    const checkUsageTeamMembersRes = await checkUsageTeamMembers(email);
    if (checkUsageTeamMembersRes.status !== 'ok')
      return { status: false, errType: InviteTeamMemberErrorType.CheckUsageTeamMembers };

    if (checkUsageTeamMembersRes.rooms <= 0) {
      const addTeamSeatsRes = await addTeamSeats();
      if (addTeamSeatsRes.status !== 'ok') return { status: false, errType: InviteTeamMemberErrorType.AddTeamSeats };
    }

    const addTeamMemberRes = await addTeamMember(email);
    if (addTeamMemberRes.status !== 'ok') return { status: false, errType: InviteTeamMemberErrorType.AddTeamMember };
  } catch (error) {
    return { status: false, errType: InviteTeamMemberErrorType.OtherError };
  }
  return { status: true, errType: '' };
};

type ShareNotifyToTeamMembersResponse = { status: 'ok' | 'fail' };

/**
 * Notify to team members with the description when team share is enabled
 * @param id
 * @param message
 * @returns
 */
export const shareNotifyToTeamMembers = async (id: string, message: string) => {
  const formData = new URLSearchParams();
  formData.append('message', message);
  formData.append('multipage', '1');
  formData.append('from', 'Editor');
  formData.append('sourceType', 'E2'); // pass additional sourceType to diffrentiate e2 or e1, as e1 '/view' is still being use

  return await request<ShareNotifyToTeamMembersResponse>(`/share/notify/${id}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formData,
  });
};
